/**
 * A deliberately small Markdown renderer for note bodies.
 *
 * **It escapes first and formats second.** A note body is unbounded free text
 * a user typed, rendered on a page that also shows customer data; a renderer
 * that passed raw HTML through would be stored XSS, and handing a third-party
 * parser to `dangerouslySetInnerHTML` would need the same audit anyway.
 *
 * Supported, because that is what people actually type into a call note:
 * headings, bold, italic, inline code, fenced code, links, bullet and numbered
 * lists, blockquotes, and `@mentions`. Everything else renders as literal text.
 *
 * §9: links render `rel="noopener noreferrer"` and only `https://` is
 * linkified — never `javascript:` or `data:`.
 */

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** `@sarah.chen` — in-app only, no email (Phase 4, Notes). */
const MENTION = /(^|\s)@([a-z0-9._-]{2,64})/gi;

/** A sentinel for a span of inline code, held out of the formatting passes. */
const CODE_OPEN = 'MDCODE';
const CODE_CLOSE = 'ENDCODE';

function inline(text: string): string {
  let out = escapeHtml(text);

  // Code first: nothing inside a span of code is formatted.
  const codeSpans: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_match, code: string) => {
    codeSpans.push(code);
    return `${CODE_OPEN}${codeSpans.length - 1}${CODE_CLOSE}`;
  });

  out = out.replace(
    /\[([^\]]+)\]\((https:\/\/[^\s)]+)\)/g,
    (_m, label: string, href: string) =>
      `<a href="${href}" target="_blank" rel="noopener noreferrer" ` +
      `class="underline underline-offset-2">${label}</a>`,
  );
  out = out.replace(
    /(^|[\s(])(https:\/\/[^\s<)]+)/g,
    (_m, lead: string, href: string) =>
      `${lead}<a href="${href}" target="_blank" rel="noopener noreferrer" ` +
      `class="underline underline-offset-2">${href}</a>`,
  );

  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');

  out = out.replace(
    MENTION,
    '$1<span class="rounded bg-sky-50 px-1 text-sky-900" data-mention="$2">@$2</span>',
  );

  out = out.replace(
    new RegExp(`${CODE_OPEN}(\\d+)${CODE_CLOSE}`, 'g'),
    (_m, index: string) =>
      `<code class="rounded bg-muted px-1 py-0.5 text-[0.85em]">${codeSpans[Number(index)] ?? ''}</code>`,
  );

  return out;
}

export function renderMarkdown(source: string): string {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];

  let listType: 'ul' | 'ol' | null = null;
  let inCode = false;
  let codeBuffer: string[] = [];

  const closeList = (): void => {
    if (listType !== null) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };

  const flushCode = (): void => {
    html.push(
      `<pre class="overflow-x-auto rounded bg-muted p-2 text-xs"><code>${escapeHtml(
        codeBuffer.join('\n'),
      )}</code></pre>`,
    );
    codeBuffer = [];
  };

  for (const line of lines) {
    if (/^```/.test(line)) {
      if (inCode) {
        flushCode();
        inCode = false;
      } else {
        closeList();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuffer.push(line);
      continue;
    }

    if (line.trim() === '') {
      closeList();
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      closeList();
      const level = Math.min(6, heading[1]!.length + 2);
      html.push(`<h${level} class="mt-2 font-semibold">${inline(heading[2]!)}</h${level}>`);
      continue;
    }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      closeList();
      html.push(
        `<blockquote class="border-l-2 border-border pl-3 text-muted-foreground">${inline(
          quote[1]!,
        )}</blockquote>`,
      );
      continue;
    }

    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      if (listType !== 'ul') {
        closeList();
        html.push('<ul class="list-disc space-y-0.5 pl-5">');
        listType = 'ul';
      }
      html.push(`<li>${inline(bullet[1]!)}</li>`);
      continue;
    }

    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (numbered) {
      if (listType !== 'ol') {
        closeList();
        html.push('<ol class="list-decimal space-y-0.5 pl-5">');
        listType = 'ol';
      }
      html.push(`<li>${inline(numbered[1]!)}</li>`);
      continue;
    }

    closeList();
    html.push(`<p>${inline(line)}</p>`);
  }

  if (inCode) flushCode();
  closeList();

  return html.join('\n');
}
